/**
 * Presentation Layer - Language Switcher
 * UI component for switching between locales
 *
 * NOTE: Locale labels are displayed as uppercase codes (e.g., "EN", "AR")
 * directly from siteConfig.locales - no translation needed.
 * Adding a new locale to NEXT_PUBLIC_SUPPORTED_LOCALES automatically adds it here.
 *
 * Behavior:
 * - 2 locales: Shows side-by-side buttons
 * - 3+ locales: Shows a dropdown menu
 */

"use client";

import { useLocale } from "next-intl";
import { ChevronDown, Globe } from "lucide-react";
import { siteConfig } from "@/config/site";
import { cn } from "@/shared/core/utils";
import { COOKIE_NAMES, CSS_CLASSES } from "@/constants";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

export function LanguageSwitcher({ className }: { className?: string }) {
  const locale = useLocale();
  const locales = siteConfig.locales;

  const switchLanguage = (newLocale: string) => {
    document.cookie = `${COOKIE_NAMES.LOCALE}=${newLocale}`;
    window.location.reload();
  };

  // Show buttons for 2 or fewer locales
  if (locales.length <= 2) {
    return (
      <div className={cn("flex items-center gap-1", className)}>
        {locales.map((lang) => (
          <Button
            key={lang}
            variant={locale === lang ? "default" : "ghost"}
            size="sm"
            onClick={() => switchLanguage(lang)}
            className={cn(
              "text-xs sm:text-sm",
              locale === lang
                ? CSS_CLASSES.ACTIVE_BUTTON
                : CSS_CLASSES.INACTIVE_BUTTON
            )}
            disabled={locale === lang}
          >
            {lang.toUpperCase()}
          </Button>
        ))}
      </div>
    );
  }

  // Show dropdown for 3+ locales
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn("text-xs sm:text-sm gap-1", className)}
        >
          <Globe className="h-4 w-4" />
          {locale.toUpperCase()}
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {locales.map((lang) => (
          <DropdownMenuItem
            key={lang}
            onClick={() => switchLanguage(lang)}
            disabled={locale === lang}
            className={cn(
              "cursor-pointer",
              locale === lang && "bg-accent font-medium"
            )}
          >
            {lang.toUpperCase()}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
